'use client';

import { Button, CTA } from '@/components';
import { useDeviceSize } from '@/hooks/useDeviceSize';
import { Element } from '@vuddy/components';
import { useState } from 'react';
import { toast } from 'react-toastify';
import * as toastStyles from '../physical/cartToast.css';
import { VerificationCodeModal } from '../physical/VerificationCodeModal';
import * as styles from './classPurchaseBottomSheet.css';
import type { ClassVoucher } from './classVoucherContext';
import {
  ClassVoucherProvider,
  useClassVoucherSelection,
  type ClassVoucherSelectionMode,
} from './classVoucherContext';
import { ClassVoucherList } from './ClassVoucherList';

interface ClassPurchaseBottomSheetProps {
  vouchers: ClassVoucher[];
  selectionMode?: ClassVoucherSelectionMode;
  initialSelectedIds?: string[];
  initialVerifiedVoucherCodes?: Map<string, string>;
  disabledVoucherIds?: string[];
  requireSelection?: boolean;
  onSelectionChange?: (selectedIds: string[]) => void;
  onConfirm?: (
    selectedIds: string[],
    selectedVouchers: ClassVoucher[],
    verifiedVoucherCodes: Map<string, string>
  ) => void;
  onClose?: VoidFunction;
  title?: string;
}

const BottomSheetContent = ({
  vouchers,
  initialVerifiedVoucherCodes,
  disabledVoucherIds,
  requireSelection = true,
  onConfirm,
  onSelectionChange,
  onClose,
  title,
}: Pick<
  ClassPurchaseBottomSheetProps,
  | 'vouchers'
  | 'initialVerifiedVoucherCodes'
  | 'disabledVoucherIds'
  | 'requireSelection'
  | 'onConfirm'
  | 'onSelectionChange'
  | 'onClose'
  | 'title'
>) => {
  const { selectedIds } = useClassVoucherSelection();

  const selectedVouchers = vouchers.filter(voucher =>
    selectedIds.includes(voucher.id)
  );

  const isMobile = useDeviceSize() === 'mobile';

  const ctaLabel = isMobile ? '이용권 구매하기' : '이용권 선택하기';

  const isDisabled = requireSelection && selectedIds.length === 0;

  // 코드 검증 상태
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [selectedVoucherIdForCode, setSelectedVoucherIdForCode] = useState<
    string | null
  >(null);
  // key: voucherId, value: 검증된 promotionCode
  const [verifiedVoucherCodes, setVerifiedVoucherCodes] = useState<
    Map<string, string>
  >(() => new Map(initialVerifiedVoucherCodes));

  const handleConfirm = () => {
    if (isDisabled) return;

    const unverifiedSelected = selectedIds.filter(id => {
      const voucher = vouchers.find(v => v.id === id);
      return voucher?.requiresPromotionCode && !verifiedVoucherCodes.has(id);
    });
    if (unverifiedSelected.length > 0) {
      toast.info('코드를 입력해주세요.', {
        className: toastStyles.verificationCodeToast,
      });
      return;
    }

    // 확인 버튼을 눌렀을 때만 부모 상태 업데이트
    onSelectionChange?.(selectedIds);
    onConfirm?.(selectedIds, selectedVouchers, verifiedVoucherCodes);

    onClose?.();
  };

  return (
    <Element className={styles.bottomSheetContainer({ hasTitle: !!title })}>
      <Element className={styles.header}>
        {title ? (
          <Element
            as="h2"
            typography="bodySmallAccent"
            className={styles.title}
            textAlign="center">
            {title}
          </Element>
        ) : null}
      </Element>
      <Element className={styles.listWrapper({ hasTitle: !!title })}>
        <ClassVoucherList
          vouchers={vouchers}
          disabledVoucherIds={disabledVoucherIds}
          onCodeInputClick={voucherId => {
            setSelectedVoucherIdForCode(voucherId);
            setVerificationModalOpen(true);
          }}
          verifiedVoucherCodes={verifiedVoucherCodes}
        />
      </Element>
      <Element style={{ marginTop: 6 }}>
        <CTA direction="vertical" position="sticky" hasPadding={false}>
          <Button
            variant={isDisabled ? 'solidTertiary' : 'solidPrimary'}
            size="large"
            fullWidth
            disabled={isDisabled}
            onClick={handleConfirm}>
            {ctaLabel}
          </Button>
        </CTA>
      </Element>
      <VerificationCodeModal
        isOpen={verificationModalOpen}
        onClose={() => {
          setVerificationModalOpen(false);
          setSelectedVoucherIdForCode(null);
        }}
        onVerified={codeName => {
          toast.success('코드 검증이 완료되었습니다.', {
            className: toastStyles.verificationCodeToast,
          });
          if (selectedVoucherIdForCode) {
            setVerifiedVoucherCodes(prev =>
              new Map(prev).set(selectedVoucherIdForCode, codeName)
            );
          }
          setVerificationModalOpen(false);
          setSelectedVoucherIdForCode(null);
        }}
        productId={
          selectedVoucherIdForCode ? Number(selectedVoucherIdForCode) : 0
        }
        initialCode={
          selectedVoucherIdForCode
            ? verifiedVoucherCodes.get(selectedVoucherIdForCode)
            : undefined
        }
      />
    </Element>
  );
};

export const ClassPurchaseBottomSheet = ({
  vouchers,
  selectionMode = 'multiple',
  initialSelectedIds,
  initialVerifiedVoucherCodes,
  disabledVoucherIds,
  requireSelection,
  onSelectionChange,
  onConfirm,
  onClose,
  title,
}: ClassPurchaseBottomSheetProps) => {
  return (
    <ClassVoucherProvider
      selectionMode={selectionMode}
      initialSelectedIds={initialSelectedIds}>
      <BottomSheetContent
        vouchers={vouchers}
        initialVerifiedVoucherCodes={initialVerifiedVoucherCodes}
        disabledVoucherIds={disabledVoucherIds}
        requireSelection={requireSelection}
        onConfirm={onConfirm}
        onSelectionChange={onSelectionChange}
        onClose={onClose}
        title={title}
      />
    </ClassVoucherProvider>
  );
};
